import React, { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory, Link } from 'react-router-dom'
import DateTimeFunctions from '~/Classes/dateTimeFunctions'
import FormatNumberFuncs from '~/Classes/numberFormatFunctions'
import Alert from '~/Common-Objects/UI/Alert'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import Icons from '~/Common-Objects/UI/Icons/index'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import Paper from '~/Common-Objects/UI/Paper/Paper'
import SmallLineGraph from '~/Common-Objects/UI/Graphs/SmallLineGraph/SmallLineGraph'
import SquareChip from '~/Common-Objects/UI/Chips/SquareChip.jsx'
import Stripe from '~/Common-Objects/UI/Payment/stripe'
import TextInput from '~/Common-Objects/UI/Forms/TextInput/TextInput'
import Toggle from '~/Common-Objects/UI/Forms/Toggle/Toggle'
import axios from '~/data/http/axios'
import PermissionChecker from '~/Classes/permissions'
import EmailValidator from '~/Classes/emailValidator'
import UpgradeDrawerTiers from '~/pages/cp/includes/upgrade/upgradeTiers'
import UpgradeDrawer from '~/pages/cp/includes/upgrade/upgrade'
import { alterSiteDrawer, alterSnackbar } from '~/data/actions/siteActions'
import CloseAccountDrawer from '../closeAccountDrawer'
import SmsUpsell from './smsUpsell'
import ManageSms from '../../includes/upgrade/sms'

export default function SubscriptionPage() {
    const site = useSelector((state) => state.siteMaster)
    const user = useSelector((state) => state.user)
    const [account, setAccount] = useState(null)
    const [currentPlan, setCurrentPlan] = useState(null)
    const [nextPlan, setNextPlan] = useState(null)
    const [smsPlan, setSmsPlan] = useState(null)
    const [nextSmsPlan, setNextSmsPlan] = useState(null)
    const [liveActiveCount, setLiveActiveCount] = useState(null)
    const permissions = useSelector((state) => state.permission.permissions)
    const history = useHistory()
    const dispatch = useDispatch()
    const readOnlyAgency = site.showBillingReadOnly
    const creditAccount = account?.billingFrequency === 2

    const writeAccess = PermissionChecker('company', permissions, 'write') && !readOnlyAgency

    const getAccount = async () => {
        try {
            const res = await axios.get('/accountMaster?recount=true')
            setAccount(res.data.AccountMaster)
        } catch (error) {
            console.error('Error fetching account:', error)
        }
    }

    const saveOptions = async (label, value) => {
        const options = account.options || {}
        options[label] = value
        let error = ''
        if (options.emailRenewals) {
            if (!EmailValidator.validateEmail(options.emailRenewalsAddress)) {
                error = 'Invalid Email'
            }
        }
        if (error) {
            return
        }
        await axios.put('/accountMaster', { ...account, options })
        dispatch(alterSnackbar(true, 'Invoice Settings Saved'))
    }

    const changePlan = async () => {
        var drawerContent = (
            <UpgradeDrawer
                close={() => {
                    dispatch(alterSiteDrawer(false, true, 'right', null))
                    getAccount()
                    getUsage()
                }}
            />
        )

        if (account.billedOnContacts || site.siteId === 1) {
            drawerContent = (
                <UpgradeDrawerTiers
                    history={history}
                    close={() => {
                        dispatch(alterSiteDrawer(false, true, 'right', null))
                        getAccount()
                        getUsage()
                    }}
                />
            )
        }

        dispatch(alterSiteDrawer(true, true, 'right', drawerContent, true, '1000px', null, null, true))
    }

    const getUsage = async () => {
        try {
            const res = await axios.get('/accountMaster/usage')
            setNextPlan(res.data.NextBilling)
            setSmsPlan(res.data.SmsPlan)
            setNextSmsPlan(res.data.NextSmsPlan)
            setCurrentPlan(res.data.Billing)
            setLiveActiveCount(res.data.liveActiveCount)
        } catch (e) {
            console.error('Error fetching usage:', e)
        }
    }

    const closeAccount = () => {
        const drawer = (
            <CloseAccountDrawer
                close={() => {
                    dispatch(alterSiteDrawer(false, true, 'right', null))
                    getUsage()
                    getAccount()
                }}
            />
        )
        return dispatch(alterSiteDrawer(true, true, 'right', drawer, true, 600, null, null, true))
    }

    useEffect(() => {
        getAccount()
        getUsage()
    }, [])

    if (!account || !currentPlan || liveActiveCount === null) return <LoadingIcons />

    return (
        <div>
            <div className='row'>
                <div className={readOnlyAgency ? 'col-xs-12 col-sm-12' : 'col-xs-12 col-sm-12 col-md-8'}>
                    <CurrentPlan
                        account={account}
                        currentPlan={currentPlan}
                        nextPlan={nextPlan}
                        changePlan={changePlan}
                        writeAccess={writeAccess}
                        liveActiveCount={liveActiveCount}
                    />
                    {!creditAccount && (
                        <Usage
                            account={account}
                            writeAccess={writeAccess}
                            currentPlan={currentPlan}
                            smsPlan={smsPlan}
                            changePlan={changePlan}
                        />
                    )}
                    {site.siteId === 1 && (
                        <Sms
                            writeAccess={writeAccess}
                            account={account}
                            smsPlan={smsPlan}
                            nextSmsPlan={nextSmsPlan}
                            refresh={async () => {
                                await Promise.all([getUsage(), getAccount()])
                            }}
                        />
                    )}
                    {!readOnlyAgency && (
                        <CloseAccount closeAccount={closeAccount} writeAccess={user?.userData?.roleId === 1} />
                    )}
                </div>
                {!readOnlyAgency && (
                    <div className='col-xs-12 col-sm-12 col-md-4'>
                        <PaymentMethods writeAccess={writeAccess} />
                        {writeAccess && <Renewal account={account} setAccount={setAccount} saveOptions={saveOptions} />}
                    </div>
                )}
            </div>
        </div>
    )
}

function getEligibleDiscountPercent(account, currentPlan) {
    if (typeof currentPlan?.discountPercent === 'number' && currentPlan.discountPercent > 0) {
        return currentPlan.discountPercent
    }
    const voucherPct = Number(account.options?.permanentDiscountPct)
    if (voucherPct > 0 && voucherPct <= 100) return voucherPct
    if (account.billingFrequency === 1) return 10
    return 0
}

function CurrentPlan({ account, currentPlan, nextPlan, writeAccess, changePlan = () => {}, liveActiveCount }) {
    const tier = useSelector((state) => state.accountMaster.tier)
    const site = useSelector((state) => state.siteMaster)
    const readOnlyAgency = site.showBillingReadOnly
    const creditAccount = account.billingFrequency === 2
    const discountPercent = getEligibleDiscountPercent(account, currentPlan)
    return (
        <Paper className='paper-padding-24'>
            <div className='row'>
                <div className='col-xs-12 col-sm-12 col-md-3'>
                    <SubHeading style={{ marginBottom: 4 }}>Your Plan</SubHeading>
                    {!creditAccount && (
                        <Heading>
                            {tier.name} - {FormatNumberFuncs.formatNumber(currentPlan.maxUniqueSends)}
                        </Heading>
                    )}
                    {creditAccount && (
                        <Heading>Pay-As-You-Go - {FormatNumberFuncs.formatNumber(account.credits)} Credits</Heading>
                    )}
                </div>
                <div className='col-xs-12 col-sm-12 col-md-3'>
                    {!creditAccount && (
                        <>
                            <SubHeading style={{ marginBottom: 4 }}>Next Billing Date</SubHeading>
                            <Heading>{DateTimeFunctions.formatDateTime(account.nextBillDate, '2')}</Heading>
                        </>
                    )}
                </div>
                <div className='col-xs-12 col-sm-12 col-md-3'>
                    {!creditAccount && (
                        <>
                            <SubHeading style={{ marginBottom: 4 }}>
                                Next {account.billingFrequency === 1 ? 'Annual ' : ''}Payment
                            </SubHeading>
                            <Heading>
                                {currentPlan.symbol}
                                {FormatNumberFuncs.formatNumber(currentPlan.friendlyPrice, 2)}
                                {account.addVAT ? ` + VAT` : ''}
                            </Heading>
                            {discountPercent > 0 && (
                                <SubHeading style={{ marginTop: 4 }}>
                                    Includes a {discountPercent}% discount
                                </SubHeading>
                            )}
                        </>
                    )}
                </div>
                {!readOnlyAgency && (
                    <div
                        className='col-xs-12 col-sm-12 col-md-3'
                        style={{ display: 'flex', alignItems: 'center', zIndex: 999 }}
                    >
                        <Button
                            style={{ margin: '0 0 0 auto' }}
                            className='button-primary button-sml'
                            onClick={changePlan}
                            disabled={!writeAccess}
                            tooltip={
                                !writeAccess &&
                                `<div style="text-align:center;">
                                You do not have access to manage your ${site.siteName} subscription.
                                <br />
                                Please contact your account admin.
                            </div>`
                            }
                            for='manageSubscription'
                            iconLeft={!writeAccess && <Icons icon='padlock' style={{ height: 16, width: 16 }} />}
                            postHogTag='subscription-manage'
                        >
                            Manage Subscription
                        </Button>
                    </div>
                )}
            </div>
            {!!nextPlan && liveActiveCount <= nextPlan.maxUniqueSends && (
                <Alert
                    className='mar-t24'
                    type='info'
                    header={`Your ${site.siteName} plan is changing`}
                    content={
                        <>
                            <div>
                                On <strong>{DateTimeFunctions.formatDateTime(account.nextBillDate, '2')}</strong> your
                                account is scheduled to downgrade to{' '}
                                <strong>
                                    {nextPlan.AccountBillingTier?.name} -{' '}
                                    {FormatNumberFuncs.formatNumber(nextPlan.maxUniqueSends)}
                                </strong>{' '}
                                for {nextPlan.symbol}
                                {nextPlan.friendlyPrice} per month.
                            </div>
                            <div>
                                You currently have{' '}
                                <strong>{FormatNumberFuncs.formatNumber(liveActiveCount)} active contacts.</strong> We
                                will review your active contacts on the{' '}
                                {DateTimeFunctions.formatDateTime(account.nextBillDate, '2')}. You can manage your
                                active contact count using the{' '}
                                <Link to='/cp/subscribers?segment=Active'>Active Contacts segment</Link> in Contacts.
                            </div>
                        </>
                    }
                ></Alert>
            )}
            {!!nextPlan && liveActiveCount > nextPlan.maxUniqueSends && (
                <Alert
                    className='mar-t24'
                    type='warning'
                    header='Your scheduled downgrade is at risk'
                    content={
                        <>
                            <div>
                                On <strong>{DateTimeFunctions.formatDateTime(account.nextBillDate, '2')}</strong> your
                                account is scheduled to downgrade to{' '}
                                <strong>
                                    {nextPlan.AccountBillingTier?.name} -{' '}
                                    {FormatNumberFuncs.formatNumber(nextPlan.maxUniqueSends)}
                                </strong>{' '}
                                for {nextPlan.symbol}
                                {nextPlan.friendlyPrice} per month. Your plan will only downgrade successfully if your
                                active contacts are below {FormatNumberFuncs.formatNumber(nextPlan.maxUniqueSends)} on
                                this date.
                            </div>
                            <div>
                                You currently have{' '}
                                <strong>{FormatNumberFuncs.formatNumber(liveActiveCount)} active contacts.</strong> We
                                will review your active contacts on the{' '}
                                {DateTimeFunctions.formatDateTime(account.nextBillDate, '2')}. You can manage your
                                active contact count using the{' '}
                                <Link to='/cp/subscribers?segment=Active'>Active Contacts segment</Link> in Contacts.
                            </div>
                        </>
                    }
                />
            )}
        </Paper>
    )
}

function Usage({ account, currentPlan, smsPlan, writeAccess, changePlan = () => {} }) {
    const site = useSelector((state) => state.siteMaster)
    const overLimit = account.totalActiveSubscribers > currentPlan.maxUniqueSends
    const overDeleteLimit = account.totalDeletedSubscribers > currentPlan.maxUniqueSends
    return (
        <Paper className='paper-padding-24'>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                {currentPlan.type === 0 && !!currentPlan.friendlyPrice && account.billedOnContacts && (
                    <>
                        {overLimit && (
                            <Alert
                                type='error'
                                header='You have exceeded your monthly contact allowance'
                                content={`To ensure you don't lose access to ${site.siteName} and can continue to communicate with your contacts, you will need to upgrade your account.`}
                                cta={
                                    writeAccess && (
                                        <a
                                            onClick={changePlan}
                                            className='quickFlex'
                                            style={{ gap: 2, alignItems: 'center' }}
                                        >
                                            Upgrade Your Account{' '}
                                            <Icons
                                                icon='arrowRight'
                                                style={{ height: 16, width: 16, margin: 0 }}
                                            />
                                        </a>
                                    )
                                }
                            ></Alert>
                        )}

                        {overDeleteLimit && (
                            <Alert
                                type='error'
                                header='You have reached your deletion limit'
                                content={`You have deleted ${account.totalDeletedSubscribers} contacts this month which is more than your contact limit. Please upgrade your subscription to continue.`}
                                cta={
                                    writeAccess && (
                                        <a
                                            onClick={changePlan}
                                            className='quickFlex'
                                            style={{ gap: 2, alignItems: 'center' }}
                                        >
                                            Upgrade Your Account{' '}
                                            <Icons
                                                icon='arrowRight'
                                                style={{ height: 16, width: 16, margin: 0 }}
                                            />
                                        </a>
                                    )
                                }
                            ></Alert>
                        )}
                        <Progress
                            heading='Active Contacts'
                            subheading={`You have had ${FormatNumberFuncs.formatNumber(account.totalActiveSubscribers)} active contacts this month`}
                            total={currentPlan.maxUniqueSends}
                            used={account.totalActiveSubscribers}
                            icon='avatars2'
                            subsubheading={
                                !!account.totalDeletedSubscribers &&
                                `You deleted ${FormatNumberFuncs.formatNumber(account.totalDeletedSubscribers)} contacts this month. They'll count as active until ${DateTimeFunctions.formatDateTime(
                                    account.billingNextSubscriberCheck || account.nextBillDate,
                                    2
                                )}.`
                            }
                        />
                    </>
                )}
                {!account.billedOnContacts && (
                    <Progress
                        heading='Send Limit'
                        subheading={`You have sent to ${currentPlan.maxUniqueSends - account.remainingEmails} unique email addresses this month.`}
                        total={currentPlan.maxUniqueSends}
                        used={currentPlan.maxUniqueSends - account.remainingEmails}
                        icon='envelope'
                    />
                )}
                {!!smsPlan &&
                    (smsPlan.maxUniqueSends > 0 ? (
                        <Progress
                            heading='SMS Credits'
                            subheading={`You have used ${FormatNumberFuncs.formatNumber(smsPlan.maxUniqueSends - account.smsCredits)} SMS ${smsPlan.maxUniqueSends - account.smsCredits === 1 ? 'credit' : 'credits'} this month`}
                            total={smsPlan.maxUniqueSends}
                            used={smsPlan.maxUniqueSends - account.smsCredits}
                            icon='sms'
                        />
                    ) : (
                        <Progress
                            heading='SMS Credits'
                            subheading={`You have ${FormatNumberFuncs.formatNumber(account.smsCredits)} SMS ${account.smsCredits === 1 ? 'credit' : 'credits'} remaining`}
                            used={0}
                            icon='sms'
                        />
                    ))}
            </div>
        </Paper>
    )
}

function CloseAccount({ writeAccess, closeAccount = () => {} }) {
    const site = useSelector((state) => state.siteMaster)
    const user = useSelector((state) => state.user)

    console.log('user', user)
    return (
        <Paper className='paper-padding-24'>
            <div className='quickFlex' style={{ alignItems: 'center' }}>
                <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Heading>Close Your Account</Heading>
                    <SubHeading>This will close your account and you will lose access immediately.</SubHeading>
                </div>
                <Button
                    className='button-red button-sml'
                    onClick={closeAccount}
                    disabled={!writeAccess}
                    tooltip={
                        !writeAccess
                            ? `<div style="text-align:center">
                                You do not have access to manage your ${site.siteName} subscription.
                                <br />
                                Please contact your account admin.
                            </div>`
                            : undefined
                    }
                    for='closeAccount'
                    postHogTag='subscription-close-account'
                >
                    Close Your Account
                </Button>
            </div>
        </Paper>
    )
}

function PaymentMethods({ writeAccess }) {
    return (
        <Paper className='paper-padding-24'>
            <Heading style={{ marginBottom: 24 }}>Payment Methods</Heading>
            <Stripe key={1} writeAccess={writeAccess} />
        </Paper>
    )
}

function Renewal({ account, saveOptions = () => {} }) {
    const [touched, setTouched] = useState(false)
    const [emailInvoices, setEmailInvoices] = useState(account.options?.emailRenewals)
    const [invoiceEmail, setInvoiceEmail] = useState(account.options?.emailRenewalsAddress)
    const timer = useRef()

    const toggle = () => {
        setEmailInvoices(!emailInvoices)
        saveOptions('emailRenewals', !emailInvoices)
    }

    const updateEmailAddress = (event) => {
        const value = event.currentTarget.value
        setTouched(true)
        setInvoiceEmail(value)
        if (timer.current) clearTimeout(timer.current)
        if (EmailValidator.validateEmail(value)) {
            timer.current = setTimeout(() => {
                saveOptions('emailRenewalsAddress', value)
            }, 250)
        }
    }

    const validEmail = emailInvoices && EmailValidator.validateEmail(invoiceEmail)
    return (
        <Paper className='paper-padding-24'>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <Heading>Renewal Invoices</Heading>
                <Toggle
                    className='mar-r10'
                    after={'Email renewal invoices'}
                    checked={emailInvoices}
                    onChange={toggle}
                />
                {emailInvoices && (
                    <TextInput
                        outerStyle={{ flex: '1' }}
                        label='Email Address'
                        error={touched && !validEmail ? 'Invalid Email' : undefined}
                        value={invoiceEmail}
                        placeholder='email@address.com'
                        type='email'
                        onChange={updateEmailAddress}
                    />
                )}
            </div>
        </Paper>
    )
}

function Sms({ account, smsPlan, nextSmsPlan, writeAccess, refresh = () => {} }) {
    const site = useSelector((state) => state.siteMaster)
    const dispatch = useDispatch()

    const manage = () => {
        const content = (
            <ManageSms
                onFinish={async () => {
                    await refresh()
                    dispatch(alterSiteDrawer(false, true, 'right', null))
                }}
            />
        )
        dispatch(alterSiteDrawer(true, true, 'right', content, true, 500, null, null, true))
    }

    if (
        (!smsPlan || smsPlan.maxUniqueSends === 0) &&
        (account.country === 'United Kingdom' || account.options?.allowSms)
    ) {
        return <SmsUpsell refresh={refresh} writeAccess={writeAccess} />
    }

    if (!smsPlan) return null

    return (
        <Paper className='paper-padding-24'>
            <div className='quickFlex mar-b16' style={{ justifyContent: 'space-between' }}>
                <Heading>SMS Add-On</Heading>
                {nextSmsPlan?.id === -1 && (
                    <SquareChip
                        style={{ zIndex: 99 }}
                        className='square-chip-red'
                        tooltip={`Your SMS subscription will end on the ${DateTimeFunctions.formatDateTime(account.nextBillDate, '2')}.<br/>Your current credits will be available until this date.`}
                        for='smsEndingSoon'
                        iconLeft={<Icons icon='info' style={{ height: 16, width: 16 }} />}
                    >
                        Ending Soon
                    </SquareChip>
                )}
                {nextSmsPlan?.id > 0 && (
                    <SquareChip
                        style={{ zIndex: 99 }}
                        className='square-chip-yellow'
                        tooltip={`Your SMS subscription will downgrade to ${FormatNumberFuncs.formatNumber(nextSmsPlan.maxUniqueSends)} for ${nextSmsPlan.symbol}${FormatNumberFuncs.formatNumber(nextSmsPlan.price)}${account.addVAT ? ' (+VAT)' : ''} on the ${DateTimeFunctions.formatDateTime(account.nextBillDate, '2')}.`}
                        for='smsEndingSoon'
                        iconLeft={<Icons icon='info' style={{ height: 16, width: 16 }} />}
                    >
                        Downgrade Scheduled
                    </SquareChip>
                )}
            </div>
            <Paper className='paper-padding-16 no-marg'>
                <div className='row middle-md'>
                    <div className='col-xs-12 col-sm-12 col-md-4'>
                        <SubHeading>Monthly Credits</SubHeading>
                        <Heading>{FormatNumberFuncs.formatNumber(smsPlan.maxUniqueSends)} SMS credits</Heading>
                    </div>
                    <div className='col-xs-12 col-sm-12 col-md-4'>
                        <SubHeading>{account.billingFrequency === 1 ? 'Annual' : 'Monthly'} Cost</SubHeading>
                        <Heading>
                            {smsPlan.symbol}
                            {FormatNumberFuncs.formatNumber(smsPlan.price)}
                            {account.addVAT ? ` + VAT` : ''}
                        </Heading>
                    </div>
                    <div className='col-xs-12 col-sm-12 col-md-4 end-md'>
                        <Button
                            className='button-primary button-sml'
                            disabled={!writeAccess}
                            onClick={manage}
                            tooltip={
                                !writeAccess &&
                                `<div style="text-align:center">
                                You do not have access to manage your ${site.siteName} subscription.
                                <br />
                                Please contact your account admin.
                            </div>`
                            }
                            for='manageSmsSubscription'
                            iconLeft={!writeAccess && <Icons icon='padlock' style={{ height: 16, width: 16 }} />}
                        >
                            Manage SMS Subscription
                        </Button>
                    </div>
                </div>
            </Paper>
        </Paper>
    )
}

function Heading({ children, style }) {
    return (
        <div
            style={{
                fontWeight: 600,
                fontStyle: 'Semi Bold',
                fontSize: '16px',
                leadingTrim: 'NONE',
                lineHeight: '24px',
                letterSpacing: '-0.24px',
                color: 'var(--neutral-950)',
                ...style
            }}
        >
            {children}
        </div>
    )
}

function SubHeading({ children, style }) {
    return (
        <div
            style={{
                fontWeight: 400,
                fontStyle: 'Regular',
                fontSize: '13px',
                leadingTrim: 'NONE',
                lineHeight: '20px',
                letterSpacing: '-0.1px',
                color: 'var(--neutral-800)',
                ...style
            }}
        >
            {children}
        </div>
    )
}

function Progress({ heading, total, used, icon, subheading, subsubheading }) {
    const siteMaster = useSelector((state) => state.siteMaster)
    return (
        <div className='quickFlex' style={{ alignItems: 'center', gap: 16 }}>
            <Icons circle={true} icon={icon} />
            <div className='quickFlex' style={{ gap: 8, flexGrow: 1, flexDirection: 'column' }}>
                <div className='quickFlex' style={{ justifyContent: 'space-between', width: '100%' }}>
                    <Heading>{heading}</Heading>
                    {total !== undefined && (
                        <Heading>
                            {FormatNumberFuncs.formatNumber(used)} / {FormatNumberFuncs.formatNumber(total)}
                        </Heading>
                    )}
                </div>
                <div className='quickFlex' style={{ justifyContent: 'space-between', width: '100%' }}>
                    <SmallLineGraph
                        color={siteMaster.colours.$primary}
                        width='100%'
                        height='6'
                        hideText={true}
                        val1={used}
                        val2={total}
                    />
                </div>
                <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', gap: 2, width: '100%' }}>
                    <div className='quickFlex' style={{ justifyContent: 'space-between', width: '100%' }}>
                        <SubHeading>{subheading}</SubHeading>
                        {total !== undefined && (
                            <SubHeading>{FormatNumberFuncs.formatNumber(total - used)} remaining</SubHeading>
                        )}
                    </div>
                    {!!subsubheading && (
                        <div className='quickFlex' style={{ justifyContent: 'space-between', width: '100%' }}>
                            <SubHeading>{subsubheading}</SubHeading>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
